/**
 * The four named VRoid heroes. One per class: the class you pick is "you", the other three are
 * your bot companions. Names are UI-only (the sim unit `name`); the VRM file is the one the toon
 * renderer already maps per class (render/toon/assets.ts HERO_VRM).
 */
import type { ClassId, Role } from '@keystone/sim';
import { roleOf } from '@keystone/progression';
import { HERO_VRM } from '../render/toon/heroModels';
import { ROLE_COLOR } from '../render/palette';

export interface HeroDef {
  classId: ClassId;
  /** Display name ("Fumiriya"). */
  name: string;
  /** Epithet under the name. */
  title: string;
  /** VRM model id (assets/…/<vrm>.vrm). */
  vrm: string;
  /** Accent colour for cards. */
  accent: string;
  /** Finer role label than Role ("Melee DPS"). */
  roleLabel: string;
}

const HEROES: Readonly<Record<string, Omit<HeroDef, 'classId' | 'vrm' | 'accent'>>> = {
  warden: { name: 'Fumiriya', title: 'Shield of the Vanguard', roleLabel: 'Tank' },
  lightbinder: { name: 'Hikari', title: 'Dawnlight Mender', roleLabel: 'Healer' },
  blademaster: { name: 'Kaede', title: 'Crimson Blade', roleLabel: 'Melee DPS' },
  stormcaller: { name: 'Shino', title: 'Voice of the Tempest', roleLabel: 'Ranged DPS' },
  // +classes: the five base classes (the Group Finder names companions after these heroes).
  warrior: { name: 'Fumiriya', title: 'Shield of the Vanguard', roleLabel: 'Tank' },
  acolyte: { name: 'Hikari', title: 'Dawnlight Mender', roleLabel: 'Healer' },
  rogue: { name: 'Kaede', title: 'Crimson Blade', roleLabel: 'Melee DPS' },
  mage: { name: 'Shino', title: 'Voice of the Arcane', roleLabel: 'Ranged DPS' },
  ranger: { name: 'Sayo', title: 'Eye of the Wilds', roleLabel: 'Ranged DPS' },
};

const ROLE_LABEL: Readonly<Record<Role, string>> = { tank: 'Tank', healer: 'Healer', dps: 'DPS' };

/** Hero for a class (a generic fallback for classes added later). */
export function heroFor(classId: ClassId): HeroDef {
  const role = roleOf(classId);
  const h = HEROES[classId];
  return {
    classId,
    name: h?.name ?? classId[0]!.toUpperCase() + classId.slice(1),
    title: h?.title ?? '',
    vrm: HERO_VRM[classId] ?? '',
    accent: ROLE_COLOR[role],
    roleLabel: h?.roleLabel ?? ROLE_LABEL[role],
  };
}

/** Party slot order used for every run (tank, healer, dps, dps): the base classes (+classes). */
export const PARTY_ORDER: readonly ClassId[] = ['warrior', 'acolyte', 'rogue', 'mage'];
